import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import type { Goal, TeamMember } from '../components/Doelstellingen/Milestone_organizer/types';

const STORAGE_KEY = 'superkonnected-active-goal-context';

export interface AvailableGoal {
  goalId: string;
  goalTitle: string;
  role: TeamMember['role'];
  accessLevel: TeamMember['accessLevel'];
  function?: string;
}

export interface GoalContextState {
  activeGoalId: string | null;
  availableGoals: AvailableGoal[];
  isInTeamContext: boolean;
  activeGoal: AvailableGoal | null;
  setActiveGoalId: (goalId: string | null) => void;
  loading: boolean;
}

/**
 * Hook for managing goal context switching
 * Allows users to switch between their personal environment and team goals
 */
const useGoalContext = (userId: string | undefined): GoalContextState => {
  const [activeGoalId, setActiveGoalIdState] = useState<string | null>(() => {
    // Load from localStorage on mount
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored || null;
    }
    return null;
  });
  const [availableGoals, setAvailableGoals] = useState<AvailableGoal[]>([]);
  const [loading, setLoading] = useState(true);

  // Persist activeGoalId to localStorage
  const setActiveGoalId = (goalId: string | null) => {
    setActiveGoalIdState(goalId);
    if (typeof window !== 'undefined') {
      if (goalId) {
        localStorage.setItem(STORAGE_KEY, goalId);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  };

  // Query all goals where user is a team member
  useEffect(() => {
    if (!userId) {
      setAvailableGoals([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Query goals where user is in teamMembers array
    const goalsRef = collection(db, 'goals');
    
    // Unfortunately, Firestore doesn't support array-contains with specific field matching
    // So we need to fetch all goals and filter client-side
    // Alternative: maintain a separate collection for team memberships
    const q = query(goalsRef);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const goals: AvailableGoal[] = [];

        snapshot.forEach((doc) => {
          const goalData = doc.data() as Goal;
          
          // Check if user is in teamMembers array
          if (goalData.teamMembers && Array.isArray(goalData.teamMembers)) {
            const membership = goalData.teamMembers.find(
              (member) => member.userId === userId
            );

            if (membership) {
              goals.push({
                goalId: doc.id,
                goalTitle: goalData.title,
                role: membership.role,
                accessLevel: membership.accessLevel || 'all', // Default to 'all' for backwards compatibility
                function: membership.function,
              });
            }
          }
        });

        setAvailableGoals(goals);
        setLoading(false);

        // Clear active goal if it's no longer available
        if (activeGoalId && !goals.find((g) => g.goalId === activeGoalId)) {
          setActiveGoalId(null);
        }
      },
      (error) => {
        console.error('Error fetching team goals:', error);
        setAvailableGoals([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const isInTeamContext = activeGoalId !== null;
  const activeGoal = availableGoals.find((g) => g.goalId === activeGoalId) || null;

  return {
    activeGoalId,
    availableGoals,
    isInTeamContext,
    activeGoal,
    setActiveGoalId,
    loading,
  };
};

export { useGoalContext };
export default useGoalContext;
